import { useQuery } from '@apollo/client';
import { Button } from '@apollo/orbit';
import React, { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  graphSettingsRouteConfig,
  variantProposalListRouteConfig,
} from '../../routes';

import { ProposalList } from './ProposalList';
import { ProposalListPageQuery } from './ProposalListPage';
import { ProposalNotificationToggle } from './ProposalNotificationToggle';
import { ProposeChangesButton } from './ProposeChangesButton';
import { SetProposalNotificationStatusModal } from './SetProposalNotificationStatusModal';

export const VariantProposalListPage = () => {
  const { graphId, graphVariant, view } = useRouteParams(
    variantProposalListRouteConfig,
  );
  const proposalDropdownButtonRefs = useRef<{
    [name: string]: React.RefObject<HTMLButtonElement>;
  }>({});
  const [proposalDropdownButton, setProposalDropdownButton] = useState<
    'header' | 'empty'
  >('header');

  const { data } = useQuery<
    GraphQLTypes.ProposalListPageQuery,
    GraphQLTypes.ProposalListPageQueryVariables
  >(ProposalListPageQuery, {
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    variables: {
      graphId,
    },
  });

  const location = useLocation();

  const allSubgraphsByVariant = useMemo(() => {
    return Object.fromEntries(
      data?.graph?.variants.map((variant): [string, string[]] => {
        return [
          variant.name,
          variant.subgraphs?.map((subgraph) => subgraph.name) ?? [],
        ];
      }) ?? [],
    );
  }, [data?.graph?.variants]);

  return (
    <>
      <PageLayout
        title={
          <PageTitle
            title="Schema Proposals"
            tabs={
              <>
                <HeaderTab
                  isActive={() => view === 'variant'}
                  to={variantProposalListRouteConfig.locationFrom({
                    location,
                    patch: {
                      view: 'variant',
                    },
                    fromRouteConfig: variantProposalListRouteConfig,
                  })}
                  tabID={`This Variant (${graphVariant})`}
                />
                <HeaderTab
                  isActive={() => view === 'graph'}
                  tabID="This Graph"
                  to={variantProposalListRouteConfig.locationFrom({
                    location,
                    patch: {
                      view: 'graph',
                    },
                    fromRouteConfig: variantProposalListRouteConfig,
                  })}
                />
              </>
            }
            rightSide={
              <div className="flex space-x-3">
                <ProposalNotificationToggle
                  notificationStatus={data?.graph?.proposalNotificationStatus}
                />
                <PermissionGuard
                  graphId={graphId}
                  graphPermissions="canManageProposalPermissions"
                  fallback={null}
                >
                  <Button
                    variant="secondary"
                    as={Link}
                    to={graphSettingsRouteConfig.locationFrom({
                      location,
                      patch: {
                        tab: 'proposals',
                      },
                      fromRouteConfig: variantProposalListRouteConfig,
                    })}
                  >
                    Settings
                  </Button>
                </PermissionGuard>
                <ProposeChangesButton
                  ref={(el: HTMLButtonElement | null) => {
                    proposalDropdownButtonRefs.current.header = {
                      current: el,
                    };
                  }}
                  disabled={
                    (view === 'variant' &&
                      data?.graph?.variants.find((v) => v.name === graphVariant)
                        ?.isContract) ||
                    !data?.graph?.roles?.canCreateProposal
                  }
                  warningLabel={
                    data?.graph?.roles?.canCreateProposal
                      ? 'You cannot create a proposal on a contract variant. Please select a non-contract variant to create a proposal.'
                      : null
                  }
                  tooltipPlacement="start"
                  onClickMenuButton={() => setProposalDropdownButton('header')}
                  // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                  // TODO(Maya) add link to synthetics test
                  data-testid="variant_proposal_page_header_propose_changes_button"
                />
              </div>
            }
          />
        }
      >
        <ProposalList
          setProposalDropdownButton={setProposalDropdownButton}
          proposalDropdownButton={proposalDropdownButton}
          proposalDropdownButtonRefs={proposalDropdownButtonRefs}
          allSubgraphsByVariant={allSubgraphsByVariant}
        />
      </PageLayout>
      <LazyUrlModal name={Config.modals.setProposalNotificationStatus}>
        {({ closeModal, isOpen }) =>
          data?.graph ? (
            <SetProposalNotificationStatusModal
              graph={data.graph}
              isOpen={isOpen}
              onClose={closeModal}
            />
          ) : null
        }
      </LazyUrlModal>
    </>
  );
};
